import React from 'react';
import { ArrowUpRightIcon, CheckIcon, TargetIcon } from 'lucide-react';
import { CommissionStep, Role, ScorecardResult } from '../types/commission';
import { formatCurrency, nextStepForUnits, stepPayoutLabel, stepShortLabel } from '../utils/commission';
interface IncentiveTrackerProps {
  role: Role;
  units: number;
  volume: number;
  scorecard: ScorecardResult;
  payingStep: CommissionStep;
  scorecardMet: boolean;
  isCurrent: boolean;
}
export function IncentiveTracker({
  role,
  units,
  volume,
  scorecard,
  payingStep,
  scorecardMet,
  isCurrent
}: IncentiveTrackerProps) {
  const next = nextStepForUnits(role, units);
  const topStep = role.steps[role.steps.length - 1];
  const gated = role.requiresScorecard && !scorecardMet;
  const headline = role.model === 'bps' ? payingStep.bonusBps ? `+${payingStep.bonusBps} BPS earned` : 'Base rate only' : payingStep.perUnit ? `${formatCurrency(payingStep.perUnit)} per ${role.incentiveNoun ?? 'unit'}` : 'No incentive yet';
  const nextValue = next ? role.model === 'bps' ? volume * ((next.totalBps ?? 0) - (payingStep.totalBps ?? 0)) / 10000 : units * ((next.perUnit ?? 0) - (payingStep.perUnit ?? 0)) : 0;
  return <section aria-labelledby="incentive-heading" className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-6 shadow-card" data-id="element-43" data-mp="9c9ujvls5am8">
      <div data-id="element-44" data-mp="jbiwpbqstvjo">
        <p className="text-xs font-semibold uppercase tracking-widest text-brand-700" data-id="element-45" data-mp="jb96po5r7odz">
          {role.model === 'bps' ? 'Unit bonus' : 'Unit incentive'}
        </p>
        <h2 id="incentive-heading" className="mt-1.5 text-2xl font-bold text-slate-900" data-id="element-46" data-mp="s5dqxoqdu1kj">
          {gated ? 'Bonus not earned' : headline}
        </h2>
      </div>

      <div className="mt-6" data-id="element-47" data-mp="o5wnt1yfhgij">
        <div className="h-2 overflow-hidden rounded-full bg-slate-100" data-id="element-48" data-mp="122s49fh0ea8m">
          <div role="progressbar" aria-label={`${role.unitNounPlural} toward the top schedule band`} aria-valuenow={units} aria-valuemin={0} aria-valuemax={topStep.minUnits} className="h-full rounded-full bg-brand-600" style={{
          width: `${Math.min(100, units / Math.max(1, topStep.minUnits) * 100)}%`
        }} data-id="element-49" data-mp="x48lfnxppekh" />
        </div>

        <ol className="mt-4 grid gap-3" style={{
        gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))'
      }} data-id="element-50" data-mp="8hxp3pdm62ff">
          {role.steps.map(step => {
          const reached = !gated && units >= step.minUnits;
          const isActive = !gated && step.minUnits === payingStep.minUnits;
          return <li key={step.minUnits} className={`rounded-lg border px-3 py-2.5 ${isActive ? 'border-brand-600 bg-brand-50' : 'border-slate-200 bg-white'}`} data-id="element-51" data-mp="mhqla07wtaus">
                <div className="flex items-center justify-between gap-2" data-id="element-52" data-mp="2obg3gp4ox4xk">
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-53" data-mp="2t97uzi1fap18">
                    {stepShortLabel(step)}
                  </span>
                  {reached ? <CheckIcon className="h-3.5 w-3.5 shrink-0 text-brand-600" aria-hidden="true" data-id="element-54" data-mp="2gxxcgbr1pfqd" /> : null}
                </div>
                <p className={`mt-1 text-lg font-bold tabular-nums ${reached ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-55" data-mp="2bzvr1k65d4bq">
                  {stepPayoutLabel(role, step)}
                </p>
              </li>;
        })}
        </ol>
      </div>

      <div className="mt-6 flex items-start gap-2.5 rounded-lg bg-slate-50 px-4 py-3" data-id="element-56" data-mp="2zoy9gu8lv17a">
        <TargetIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" data-id="element-57" data-mp="2uplo6dxdu26t" />
        <p className="text-sm text-slate-700" data-id="element-58" data-mp="2urgbu001g8el">
          {gated ? <>Scorecard goal missed — the plan pays the base rate this period.</> : next && isCurrent ? <>
              <span className="font-semibold text-slate-900" data-id="element-59" data-mp="1f8uxotj6i4y8">
                {next.minUnits - units} more {role.unitNounPlural}
              </span>{' '}
              unlocks {stepPayoutLabel(role, next)}, worth about{' '}
              <span className="font-semibold tabular-nums text-slate-900" data-id="element-60" data-mp="37wrhxzhd7q43">{formatCurrency(nextValue)}</span> more.
            </> : next ? <>
              Closed {next.minUnits - units} {role.unitNounPlural} short of the {stepPayoutLabel(role, next)} band.
            </> : <>Top band reached — paying {stepPayoutLabel(role, payingStep)}.</>}
        </p>
      </div>

      <dl className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-slate-200 pt-4 text-sm" data-id="element-61" data-mp="3qtsuq6h5uuil">
        {role.requiresScorecard ? <>
            <div className="flex items-center gap-2" data-id="element-62" data-mp="3gwemuv9aze1w">
              <dt className="text-slate-500" data-id="element-63" data-mp="3gv4pk8a9y9wb">Scorecard points</dt>
              <dd className={`font-semibold tabular-nums ${scorecardMet ? 'text-slate-900' : 'text-red-600'}`} data-id="element-64" data-mp="21f4dnrxmkoe8">
                {scorecard.points.toLocaleString()}
                <span className="font-medium text-slate-400" data-id="element-65" data-mp="1xxzwqu3tzga8"> / {scorecard.goal.toLocaleString()}</span>
              </dd>
            </div>
            <a href="#/scorecard" onClick={event => event.preventDefault()} className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-brand-700 transition hover:text-brand-900" data-id="element-66" data-mp="1sydjiko0601f">
              Scorecard details
              <ArrowUpRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-67" data-mp="1f8tndxoxg113" />
            </a>
          </> : <div className="flex items-center gap-2" data-id="element-68" data-mp="1aa0b28ldmg2i">
            <dt className="text-slate-500" data-id="element-69" data-mp="1oxbjw2q6blyx">Counted on</dt>
            <dd className="font-semibold text-slate-900" data-id="element-70" data-mp="1oywcvnwuawuo">
              {role.scope} {role.unitNounPlural}
            </dd>
          </div>}
      </dl>
    </section>;
}